import type {} from "@mui/lab/themeAugmentation";
import GlobalStyles from "@mui/material/GlobalStyles";
import {
	createTheme,
	darken,
	getOverlayAlpha,
	lighten,
	PaletteOptions,
	ThemeOptions,
	ThemeProvider,
} from "@mui/material/styles";
import { useMemo } from "react";
import type {} from "@mui/x-data-grid-pro/themeAugmentation";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LicenseInfo } from "@mui/x-license-pro/utils/licenseInfo";
import CssBaseline from "@mui/material/CssBaseline";
import { useThemedColors } from "./hooks/use-themed-colors";
import { VenueTheme } from "~served/types/db";
import type {} from "@mui/x-date-pickers/themeAugmentation";

declare module "@mui/material/styles/createPalette" {
	interface Palette {
		aba: {
			main: string;
			light: string;
		};
		khqr: {
			main: string;
		};
	}
	interface PaletteOptions {
		aba: {
			main: string;
			light: string;
		};
		khqr: {
			main: string;
		};
	}
}

const commonPalettes: PaletteOptions = {
	primary: {
		main: "#de6f10",
		light: "#eb8e17",
		dark: "#d4590c",
	},
	secondary: {
		main: "#0c6dcc",
		light: "#1590f1",
		dark: "#054ead",
	},
	aba: {
		main: "#005d7b",
		light: "#10bad2",
	},
	khqr: {
		main: "#e1232e",
	},
};

const components: ThemeOptions["components"] = {
	MuiTextField: {
		defaultProps: {
			margin: "dense",
			fullWidth: true,
			variant: "outlined",
			size: "medium",
		},
	},
	MuiSwitch: {
		defaultProps: {
			size: "medium",
		},
	},
	MuiButtonGroup: {
		defaultProps: {
			variant: "contained",
			size: "medium",
		},
	},
	MuiLoadingButton: {
		defaultProps: {
			variant: "contained",
			size: "medium",
		},
	},
	MuiButton: {
		defaultProps: {
			variant: "contained",
			size: "medium",
		},
	},
	MuiIconButton: {
		defaultProps: {
			size: "medium",
		},
	},
	MuiFab: {
		defaultProps: {
			size: "medium",
		},
	},
	MuiChip: {
		defaultProps: {
			variant: "filled",
			size: "medium",
		},
	},
	MuiPaper: {
		defaultProps: {
			variant: "outlined",
		},
	},
	MuiCard: {
		defaultProps: {
			variant: "outlined",
		},
	},
	MuiDataGrid: {
		defaultProps: {
			componentsProps: {
				baseButton: {
					variant: "outlined",
					sx: {
						mr: 1,
					},
				},
			},
		},
		styleOverrides: {
			columnHeaders: ({ theme }) => ({
				backgroundColor: theme.palette.background.paper,
				color: theme.palette.text.primary,
			}),
			columnHeader: ({ theme }) => ({
				backgroundColor: theme.palette.background.paper,
			}),
		},
	},
	MuiSnackbar: {
		defaultProps: {
			anchorOrigin: {
				vertical: "bottom",
				horizontal: "right",
			},
		},
	},
	MuiAlert: {
		defaultProps: {
			variant: "standard",
		},
	},
	MuiDateTimePicker: {
		defaultProps: {
			format: "DD/MM/YY hh:mm a",
			ampm: true,
			timeSteps: {
				minutes: 1,
				seconds: 1,
			},
		},
	},
	MuiDesktopDateTimePicker: {
		defaultProps: {
			format: "DD/MM/YY hh:mm a",
			ampm: true,
			timeSteps: {
				minutes: 1,
				seconds: 1,
			},
		},
	},
	MuiMobileDateTimePicker: {
		defaultProps: {
			format: "DD/MM/YY hh:mm a",
			ampm: true,
		},
	},
	MuiDatePicker: {
		defaultProps: {
			format: "DD/MM/YY",
		},
	},
	MuiTimePicker: {
		defaultProps: {
			format: "hh:mm a",
			ampm: true,
			timeSteps: {
				minutes: 1,
				seconds: 1,
			},
		},
	},
};

const MUI_LICENSE_KEY =
	"54b15a57982fcfd357f997b69b2e34f7T1JERVI6Mzk0NzQsRVhQSVJZPTE2Nzg2MTEzNDAwMDAsS0VZVkVSU0lPTj0x";
LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

/**
 * MUI Elevation: 1-24
 */

export const getModeBasedBackgroundElevation = (mode: "light" | "dark") => {
	return mode === "light"
		? {
				BACKGROUND_DEFAULT_ELEVATION: 0,
				BACKGROUND_PAPER_ELEVATION: 1,
			}
		: {
				BACKGROUND_DEFAULT_ELEVATION: 2,
				BACKGROUND_PAPER_ELEVATION: 4,
			};
};

export const AppThemeProvider = ({
	children,
	mode = "light",
	venueTheme,
}: {
	children: JSX.Element;
	mode?: "light" | "dark";
	venueTheme?: VenueTheme;
}) => {
	const {
		primary_color,
		// primary_text_color
	} = venueTheme ?? {};
	const { BACKGROUND_DEFAULT_ELEVATION, BACKGROUND_PAPER_ELEVATION } =
		getModeBasedBackgroundElevation(mode);

	const theme = useMemo(() => {
		const mergedTheme: ThemeOptions = {
			palette: {
				mode,
				...commonPalettes,
				background:
					mode === "light"
						? {
								default: darken(
									"#ffffff",
									+getOverlayAlpha(BACKGROUND_DEFAULT_ELEVATION),
								),
								paper: darken(
									"#ffffff",
									+getOverlayAlpha(BACKGROUND_PAPER_ELEVATION),
								),
							}
						: {
								default: lighten(
									"#121212",
									+getOverlayAlpha(BACKGROUND_DEFAULT_ELEVATION),
								),
								paper: lighten(
									"#121212",
									+getOverlayAlpha(BACKGROUND_PAPER_ELEVATION),
								),
							},
			},
			components,
		};
		if (primary_color) {
			mergedTheme.palette!.primary = {
				main: primary_color,
				light: lighten(primary_color, 0.35),
				dark: darken(primary_color, 0.35),
			};
		}
		// if (primary_text_color) {
		// 	mergedTheme.palette!.text = {
		// 		primary: primary_text_color,
		// 		secondary: lighten(primary_text_color, 0.35),
		// 		disabled: lighten(primary_text_color, 0.7),
		// 	};
		// }
		return createTheme(mergedTheme);
	}, [
		mode,
		BACKGROUND_DEFAULT_ELEVATION,
		BACKGROUND_PAPER_ELEVATION,
		primary_color,
		// primary_text_color
	]);

	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<OverwriteGlobalStyles />
			<NProgressOverwriteGlobalStyles />
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				{children}
			</LocalizationProvider>
		</ThemeProvider>
	);
};

const NProgressOverwriteGlobalStyles = () => (
	<GlobalStyles
		styles={(t) => `
			#nprogress {
				pointer-events: none;
			}

			#nprogress .bar {
				background: ${t.palette.primary.main};

				position: fixed;
				z-index: 9999;
				top: 0;
				left: 0;

				width: 100%;
				height: 2px;
			}

			/* Fancy blur effect */
			#nprogress .peg {
				display: block;
				position: absolute;
				right: 0px;
				width: 100px;
				height: 100%;
				box-shadow: 0 0 10px ${t.palette.primary.main}, 0 0 5px ${t.palette.primary.main};
				opacity: 1;

				-webkit-transform: rotate(3deg) translate(0px, -4px);
				-ms-transform: rotate(3deg) translate(0px, -4px);
				transform: rotate(3deg) translate(0px, -4px);
			}

			/* Remove these to get rid of the spinner */
			#nprogress .spinner {
				display: block;
				position: fixed;
				z-index: 1031;
				top: 15px;
				right: 15px;
			}

			#nprogress .spinner-icon {
				width: 18px;
				height: 18px;
				box-sizing: border-box;

				border: solid 2px transparent;
				border-top-color: ${t.palette.primary.main};
				border-left-color: ${t.palette.primary.main};
				border-radius: 50%;

				-webkit-animation: nprogress-spinner 400ms linear infinite;
				animation: nprogress-spinner 400ms linear infinite;
			}

			.nprogress-custom-parent {
				overflow: hidden;
				position: relative;
			}

			.nprogress-custom-parent #nprogress .spinner,
			.nprogress-custom-parent #nprogress .bar {
				position: absolute;
			}

			@-webkit-keyframes nprogress-spinner {
				0% {
					-webkit-transform: rotate(0deg);
				}
				100% {
					-webkit-transform: rotate(360deg);
				}
			}
			@keyframes nprogress-spinner {
				0% {
					transform: rotate(0deg);
				}
				100% {
					transform: rotate(360deg);
				}
			}
		`}
	/>
);

const OverwriteGlobalStyles = () => {
	const { neutralGreyLight, neutralGreyDark } = useThemedColors();

	return (
		<GlobalStyles
			styles={(t) => ({
				"html,body": {
					padding: 0,
					margin: 0,
					fontFamily: "'Roboto', sans-serif",
				},
				"#__next": {
					minHeight: "100vh",
					backgroundColor: t.palette.background.default,
					color: t.palette.text.primary,
				},
				"*": {
					msOverflowStyle: "none",
					boxSizing: "border-box",
				},
				"::-webkit-scrollbar": {
					width: "12px",
					height: "12px",
					background: "transparent",
				},
				"::-webkit-scrollbar-thumb": {
					border: "2px solid transparent",
					backgroundClip: "padding-box",
					borderRadius: "8px",
					backgroundColor: "transparent",
				},
				"*:hover::-webkit-scrollbar-thumb": {
					backgroundColor: t.palette.primary.light,
				},
				"::-webkit-scrollbar-thumb:hover": {
					backgroundColor: t.palette.primary.main,
				},
				".leaflet-container": {
					backgroundColor: `${
						t.palette.mode === "light" ? neutralGreyLight : neutralGreyDark
					} !important`,
				},
			})}
		/>
	);
};
